import { z } from 'zod';

// Bump when the consent wording materially changes, so each stored record shows
// exactly which version of the notice the person agreed to.
export const CURRENT_PHOTO_CONSENT_VERSION = 'v1';

// Consent captured before a facial-recognition photo search. An adult consents
// for themselves; for a minor, a parent or guardian consents on their behalf and
// gives their name. Validated on BOTH the client (to gate the UI) and the server
// (the real enforcement point, so a crafted request cannot skip it).
export const photoConsentSchema = z
  .object({
    ageBand: z.enum(['over_18', 'under_18']),
    selfConsent: z.boolean().optional(),
    guardianName: z.string().trim().max(255).optional(),
    guardianConsent: z.boolean().optional(),
    consentVersion: z.string().min(1).max(20),
  })
  .superRefine((v, ctx) => {
    if (v.ageBand === 'over_18') {
      if (v.selfConsent !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selfConsent'],
          message: 'Please confirm you consent to us finding your photos.',
        });
      }
    } else {
      if (!v.guardianName || v.guardianName.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['guardianName'],
          message: 'A parent or guardian name is required.',
        });
      }
      if (v.guardianConsent !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['guardianConsent'],
          message: 'Parent or guardian consent is required.',
        });
      }
    }
  });

export type PhotoConsentInput = z.infer<typeof photoConsentSchema>;

export interface PhotoConsentRecord {
  ageBand: 'over_18' | 'under_18';
  isMinor: boolean;
  guardianName: string | null;
  consentVersion: string;
}

/**
 * Validate a consent payload and normalise it into the fields we persist.
 * Returns the first human-readable error message when invalid so the caller can
 * surface it directly. A minor's record keeps the guardian name; an adult's
 * never stores one.
 */
export function parsePhotoConsent(
  input: unknown,
): { ok: true; record: PhotoConsentRecord } | { ok: false; message: string } {
  const parsed = photoConsentSchema.safeParse(input);
  if (!parsed.success) {
    const message =
      parsed.error.issues[0]?.message || 'Consent is required to search for your photos.';
    return { ok: false, message };
  }
  const v = parsed.data;
  const isMinor = v.ageBand === 'under_18';
  return {
    ok: true,
    record: {
      ageBand: v.ageBand,
      isMinor,
      guardianName: isMinor ? (v.guardianName?.trim() || null) : null,
      consentVersion: v.consentVersion,
    },
  };
}
